import { Injectable } from '@angular/core';
import { defer, from, Observable, switchMap, tap } from 'rxjs';

import { AI_CREDITS_QUANTITY_STEP } from 'src/app/components/plans/plans.service';
import { PurchaseAddOnType } from './base-purchase.service';
import {
  AiCreditsEstimate,
  PurchaseAiCreditsService,
} from './purchase-ai-credits.service';
import {
  InteractiveLicenseEstimate,
  PurchaseInteractiveService,
} from './purchase-interactive.service';

type AddOnPlan = {
  productCode: string;
};

export type SelectedPurchaseAddOn = {
  type: PurchaseAddOnType;
  plan: AddOnPlan;
  quantity: number;
  /** Total price in dollars. */
  totalPrice: number;
};

@Injectable({
  providedIn: 'root',
})
export class PurchaseAddOnsService {
  private aiCreditsInitPromise?: Promise<void>;
  private interactiveInitPromise?: Promise<void>;
  private aiCreditsEstimateRequestId = 0;
  private interactiveEstimateRequestId = 0;
  private selectedAiCreditsQuantity = 0;
  private selectedInteractiveQuantity = 0;
  private selectedAiCreditsEstimate?: AiCreditsEstimate;
  private selectedInteractiveEstimate?: InteractiveLicenseEstimate;

  constructor(
    private purchaseAiCreditsService: PurchaseAiCreditsService,
    private purchaseInteractiveService: PurchaseInteractiveService,
  ) {}

  estimateAICreditsSubscription(
    quantity: number,
  ): Observable<AiCreditsEstimate> {
    const requestId = ++this.aiCreditsEstimateRequestId;
    this.selectedAiCreditsQuantity = quantity;
    this.selectedAiCreditsEstimate = undefined;

    return defer(() =>
      this._initAiCreditsService().pipe(
        switchMap(() =>
          from(
            this.purchaseAiCreditsService.estimateAnnualSubscription(quantity),
          ),
        ),
      ),
    ).pipe(
      tap((estimate) => {
        if (requestId === this.aiCreditsEstimateRequestId) {
          this.selectedAiCreditsEstimate = estimate;
        }
      }),
    );
  }

  estimateInteractiveSubscription(
    quantity: number,
  ): Observable<InteractiveLicenseEstimate> {
    const requestId = ++this.interactiveEstimateRequestId;
    this.selectedInteractiveQuantity = quantity;
    this.selectedInteractiveEstimate = undefined;

    return defer(() =>
      this._initInteractiveService().pipe(
        switchMap(() =>
          from(
            this.purchaseInteractiveService.estimateAnnualSubscription(
              quantity,
            ),
          ),
        ),
      ),
    ).pipe(
      tap((estimate) => {
        if (requestId === this.interactiveEstimateRequestId) {
          this.selectedInteractiveEstimate = estimate;
        }
      }),
    );
  }

  clearAICreditsSelection(): void {
    this.aiCreditsEstimateRequestId++;
    this.selectedAiCreditsQuantity = 0;
    this.selectedAiCreditsEstimate = undefined;
    this.purchaseAiCreditsService.form.controls.quantity.setValue(0);
  }

  clearInteractiveSelection(): void {
    this.interactiveEstimateRequestId++;
    this.selectedInteractiveQuantity = 0;
    this.selectedInteractiveEstimate = undefined;
    this.purchaseInteractiveService.form.controls.quantity.setValue(0);
  }

  getSelectedAddOns(): SelectedPurchaseAddOn[] {
    const selectedAddOns: SelectedPurchaseAddOn[] = [];
    const aiCreditsPlan = this.purchaseAiCreditsService.selectedPlan;
    const aiCreditsQuantity =
      this.selectedAiCreditsQuantity / AI_CREDITS_QUANTITY_STEP;
    const aiCreditsEstimate = this.selectedAiCreditsEstimate;
    const aiCreditsTotalPrice = aiCreditsEstimate
      ? aiCreditsEstimate.total !== undefined
        ? aiCreditsEstimate.total / 100
        : aiCreditsEstimate.amountDueNow
      : 0;

    if (aiCreditsPlan && aiCreditsQuantity > 0) {
      selectedAddOns.push({
        type: 'ai-credits',
        plan: aiCreditsPlan,
        quantity: aiCreditsQuantity,
        totalPrice: aiCreditsTotalPrice,
      });
    }

    const interactivePlan = this.purchaseInteractiveService.selectedPlan;
    const interactiveQuantity = this.selectedInteractiveQuantity;
    const interactiveTotalPrice =
      this.selectedInteractiveEstimate?.amountDueNow ?? 0;

    if (interactivePlan && interactiveQuantity > 0) {
      selectedAddOns.push({
        type: 'interactive',
        plan: interactivePlan,
        quantity: interactiveQuantity,
        totalPrice: interactiveTotalPrice,
      });
    }

    return selectedAddOns;
  }

  private _initAiCreditsService(): Observable<void> {
    if (!this.aiCreditsInitPromise) {
      this.aiCreditsInitPromise = this.purchaseAiCreditsService.init();
    }

    return from(this.aiCreditsInitPromise);
  }

  private _initInteractiveService(): Observable<void> {
    if (!this.interactiveInitPromise) {
      this.interactiveInitPromise = this.purchaseInteractiveService.init();
    }

    return from(this.interactiveInitPromise);
  }
}
